import React, { useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import KpiRow from '../shared/KpiRow';
import KpiTile from '../shared/KpiTile';
import ChartPanel from '../shared/ChartPanel';
import DateRangePicker from '../shared/DateRangePicker';
import AnalysisTable from '../shared/AnalysisTable';
import { useAnalysisRange } from '../../../hooks/analysis/useAnalysisRange';
import { useCancellationAnalysis } from '../../../hooks/analysis/useCancellationAnalysis';
import { Granularity } from '../../../utils/analytics/types';
import { formatPercentage } from '../../../utils/formatters';
import { AXIS_PROPS, CHART_INK, SERIES_COLORS } from '../shared/chartTheme';

const CancellationAnalysis: React.FC = () => {
  const { range, setRange } = useAnalysisRange();
  const [granularity, setGranularity] = useState<Granularity>('week');
  const data = useCancellationAnalysis(range, granularity);

  return (
    <div>
      <div className="analysis-controls">
        <DateRangePicker value={range} onChange={setRange} />
        <div className="analysis-toggle-group" role="group" aria-label="Granularity">
          {(['week', 'month'] as Granularity[]).map(g => (
            <button key={g} className={granularity === g ? 'active' : ''} onClick={() => setGranularity(g)}>
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <KpiRow>
        <KpiTile
          label="Cancellation rate"
          value={data.rate !== null ? formatPercentage(data.rate) : '—'}
          note="of bookings arriving in range"
        />
        <KpiTile label="Cancelled bookings" value={String(data.cancelledCount)} />
        <KpiTile
          label="Avg cancellation lead"
          value={data.avgLeadDays !== null ? `${data.avgLeadDays} days` : '—'}
          note="before check-in"
        />
        <KpiTile
          label="Freed nights resold"
          value={data.resold.resoldPct !== null ? formatPercentage(data.resold.resoldPct) : '—'}
          note={`${data.resold.resoldSiteNights} of ${data.resold.cancelledSiteNights} site-nights`}
        />
      </KpiRow>

      <ChartPanel
        title="Cancellation rate trend"
        subtitle="Cancelled ÷ booked, by scheduled arrival"
        loading={data.loading}
        error={data.error}
        empty={data.series.every(p => p.booked === 0)}
        table={{
          columns: [
            { key: 'label', label: 'Period' },
            { key: 'booked', label: 'Booked', align: 'right' },
            { key: 'cancelled', label: 'Cancelled', align: 'right' },
            { key: 'rate', label: 'Rate', align: 'right', format: v => formatPercentage(Number(v)) },
          ],
          rows: data.series,
        }}
        csv={{
          filename: `cancellation-rate-${range.from}-${range.to}`,
          columns: [
            { key: 'label', label: 'Period' },
            { key: 'booked', label: 'Booked' },
            { key: 'cancelled', label: 'Cancelled' },
            { key: 'rate', label: 'Rate %' },
          ],
          rows: data.series,
        }}
      >
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={data.series} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
            <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
            <XAxis dataKey="label" {...AXIS_PROPS} minTickGap={24} />
            <YAxis {...AXIS_PROPS} tickFormatter={v => `${v}%`} width={48} />
            <Tooltip formatter={(value: any) => [formatPercentage(Number(value)), 'Cancellation rate']} />
            <Line type="monotone" dataKey="rate" stroke={SERIES_COLORS[5]} strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
          </LineChart>
        </ResponsiveContainer>
      </ChartPanel>

      <div className="chart-panel-grid">
        <ChartPanel
          title="Cancellation lead time"
          subtitle="How far before check-in guests cancel"
          loading={data.loading}
          error={data.error}
          empty={data.leadBuckets.every(b => b.count === 0)}
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.leadBuckets} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
              <CartesianGrid stroke={CHART_INK.grid} vertical={false} />
              <XAxis dataKey="label" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} allowDecimals={false} width={36} />
              <Tooltip formatter={(value: any) => [value, 'Cancellations']} />
              <Bar dataKey="count" fill={SERIES_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </ChartPanel>

        <ChartPanel
          title="Cancellation reasons"
          loading={data.loading}
          error={data.error}
          empty={data.reasons.length === 0}
        >
          <AnalysisTable
            columns={[
              { key: 'reason', label: 'Reason' },
              { key: 'count', label: 'Count', align: 'right' },
            ]}
            rows={data.reasons}
            defaultSortKey="count"
          />
        </ChartPanel>
      </div>
    </div>
  );
};

export default CancellationAnalysis;
